import { useApplicationContext } from '@/context/v1/Application/context';
import { useKimLhc } from '@/context/KimLhc';

import { useStepMutation } from '@/components/views/Report/KimLhc/hooks/useStepMutation';
import { useUpdateStepAndCommentKimLhcReport } from '@/hooks/useUpdateStepAndCommentKimLhcReport';

import type { FormFields } from '@/components/views/Report/KimLhc/types';

interface WorkOrganizationParams {
	id: string;
	file_id: string;
	company_id: string;
	organization_id: string;
	work_organization?: string;
}

export function useWorkOrganization() {
	const { file_id, report_id, form, onNextStep, currentStep } = useKimLhc();
	const { organization, company } = useApplicationContext();
	const { mutate, isLoading: isLoadingStepMutation } = useStepMutation<WorkOrganizationParams>();
	const { mutateAsync: updateStepAndCommentKimLhcReport, isLoading: isLoadingUpdateStepAndComment } =
		useUpdateStepAndCommentKimLhcReport();

	function buildUpdatePayload(values: FormFields): WorkOrganizationParams | null {
		if (!report_id || !file_id) {
			return null;
		}

		const workOrganizationData = values.work_organization;

		return {
			id: report_id,
			file_id: file_id,
			company_id: company.id,
			organization_id: organization.id,
			work_organization: workOrganizationData.work_organization
		};
	}

	async function handleSubmit(values: FormFields): Promise<void> {
		const hasChanges = form.isFieldsTouched(['work_organization'], true);

		if (!report_id || !hasChanges) {
			await updateStepAndCommentKimLhcReport({
				id: report_id!,
				file_id: file_id!,
				step: currentStep + 1,
				organization_id: organization.id,
				company_id: company.id
			});
			onNextStep();
			return;
		}

		const payload = buildUpdatePayload(values);
		if (!payload) {
			return;
		}

		await mutate(payload);
	}

	const isLoading = isLoadingStepMutation || isLoadingUpdateStepAndComment;

	return {
		handleSubmit,
		isLoading
	};
}
